import React from 'react'
import Alert from '@mui/material/Alert'
import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import DialogContentText from '@mui/material/DialogContentText'
import FormControl from '@mui/material/FormControl'
import Grid from '@mui/material/Grid'
import InputBase from '@mui/material/InputBase'
import Paper from '@mui/material/Paper'
import Skeleton from '@mui/material/Skeleton'
import Snackbar from '@mui/material/Snackbar'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import makeStyles from '@mui/styles/makeStyles'
import { useTheme } from '@mui/material/styles'
import moment from 'moment-timezone'

import { useHistory, useParams } from 'react-router-dom'
import { useMutation, useQuery } from 'react-query'

import { FirebaseContext } from '../../utils/firebase'
import 'firebase/auth'

import { maskFromPreview, copyRevealedCredential } from '../../utils/credentialPreview'
import { API_ROOT_URL } from '../../constants/'
import * as ROUTES from '../../constants/routes'

import mailwizzLogoImg from '../../media/mailwizz_logo.png'

const TIMEZONE = moment.tz.guess() // This should be set by user configuration

const useStyles = makeStyles(theme => ({
  root: {
    position: 'absolute',
    left: 66,
    right: 0,
    width: 'calc(100% - 66px)',
    height: '100vh',
    overflowY: 'auto',
    backgroundColor: theme.palette.grey[50],
  },
  logoImg: {
    width: 100,
  },
  syncButton: {
    width: 110,
  },
  redButton: {
    color: theme.palette.error.main,
    '&:hover': {
      background: 'rgba(255,23,68,0.04)',
    },
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 250
  },
}))

const REVOKE_SUCCESS_MESSAGE = 'Account removed. Data will no longer be synced from this account. Redirecting back to settings...'
const INSERT_CONNECTOR_SUCCESS_MESSAGE = 'Connector created. Use the updated webhook URL in Mailwizz to load contact data.'
const DELETE_CONNECTOR_SUCCESS_MESSAGE = 'Connector removed. Contact data will no longer be loaded. Webhook URL has been updated.'
const WEBHOOK_URL_BASE = `https://us-central1-ltv-numbers-${process.env.REACT_APP_ENV === 'production' ? 'prod' : 'dev'}.cloudfunctions.net/webhooks/mailwizz/`

const MailwizzSource = () => {
  const theme = useTheme()
  const classes = useStyles()
  const { id } = useParams()
  const history = useHistory()
  const firebase = React.useContext(FirebaseContext)

  const [webhookUrl, setWebhookUrl] = React.useState('')
  const [showCopyTooltipApiKey, setShowCopyTooltipApiKey] = React.useState(false)
  const [showCopyTooltipWebhook, setShowCopyTooltipWebhook] = React.useState(false)

  const [timezone, setTimezone] = React.useState(TIMEZONE)
  const [message, setMessage] = React.useState('')
  const [showMessage, setShowMessage] = React.useState(false)
  const [messageSeverity, setMessageSeverity] = React.useState('success')
  const [showRevokeConfirmation, setShowRevokeConfirmation] = React.useState(false)
  const [showDeleteConnectorConfirmation, setShowDeleteConnectorConfirmation] = React.useState(false)

  const [isEditingConnector, setIsEditingConnector] = React.useState(false)

  const {
    isLoading: isLoadingIntegration,
    data: integration = {}
  } = useQuery(['integration-get', id], () =>
    firebase.auth().currentUser.getIdToken(false).then(token => {
      return fetch(`${API_ROOT_URL}/api_fs/integrations/${id}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        },
      }).then(res => res.json())
    }),
    {
      cacheTime: 0,
      staleTime: 0,
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        if (data.timezone) {
          setTimezone(data.timezone)
        }
      }
    }
  )

  const { isLoading: isUpdatingTimezone, mutate: updateTimezone } = useMutation(
    newTimezone => firebase.auth().currentUser.getIdToken(false).then(token => {
      return fetch(`${API_ROOT_URL}/api_fs/integrations/mailwizz/sync`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          integration_id: id,
          timezone: newTimezone
        }),
      }).then(res => res.json())
    }),
    {
      onSuccess: () => {
        setShowMessage(true)
        setMessage('Timezone successfully updated')
        setMessageSeverity('success')
      },
      onError: () => {
        setShowMessage(true)
        setMessage('Error updating timezone. Please try again, and if problem persists, contact support.')
        setMessageSeverity('error')
      }
    }
  )

  const {
    isLoading: isLoadingConnector,
    data: connector = {},
    refetch: refetchConnector
  } = useQuery(['connector-get', id], () =>
    firebase.auth().currentUser.getIdToken(false).then(token => {
      return fetch(`${API_ROOT_URL}/api_fs/integrations/${id}/connector`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        },
      }).then(res => res.json())
        .then(data => {
          if (data.exists) {
            return data.connector
          } else {
            return {}
          }
        })
    }),
    {
      cacheTime: 0,
      staleTime: 0,
      refetchOnWindowFocus: false,
    }
  )

  React.useEffect(() => {
    const uid = firebase.auth().currentUser.uid
    if (!integration || !connector) return
    let webhookUrl
    if (integration) {
      webhookUrl = WEBHOOK_URL_BASE + uid + '/integration/' + id
      if (connector.id) {
        webhookUrl = WEBHOOK_URL_BASE + uid + '/integration/' + id + '/connector/' + connector.id + '/contact'
      }
    }
    setWebhookUrl(webhookUrl)
  }, [integration, connector])


  const revoke = () => {
    firebase.auth().currentUser.getIdToken(false).then(token => {
      return fetch(`${API_ROOT_URL}/api_fs/integrations/mailwizz/revoke`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          integration_id: id
        })
      }).then(res => {
        setShowRevokeConfirmation(false)
        setMessage(REVOKE_SUCCESS_MESSAGE)
        setShowMessage(true)
        setMessageSeverity('success')
        setTimeout(() => history.push(ROUTES.SETTINGS), 3000)
        return res.json()
      })
    })
  }

  const insertConnector = () => {
    firebase.auth().currentUser.getIdToken(false).then(token => {
      setIsEditingConnector(true)
      return fetch(`${API_ROOT_URL}/api_fs/integrations/${id}/connector/create`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          config: {}
        })
      }).then(res => {
        setIsEditingConnector(false)
        setMessage(INSERT_CONNECTOR_SUCCESS_MESSAGE)
        setMessageSeverity('success')
        setShowMessage(true)
        refetchConnector()
        return res.json()
      })
    })
  }

  const deleteConnector = () => {
    firebase.auth().currentUser.getIdToken(false).then(token => {
      setIsEditingConnector(true)
      return fetch(`${API_ROOT_URL}/api_fs/integrations/${id}/connector/delete`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
      }).then(res => {
        setIsEditingConnector(false)
        setShowDeleteConnectorConfirmation(false)
        setMessage(DELETE_CONNECTOR_SUCCESS_MESSAGE)
        setMessageSeverity('success')
        setShowMessage(true)
        refetchConnector()
        return res.json()
      })
    })
  }

  const handleCloseMessage = (_, reason) => {
    if (reason === 'clickaway') return
    setShowMessage(false)
  }

  const handleChangeTimezone = (timezone) => {
    if (!timezone) return
    setTimezone(timezone)
    updateTimezone(timezone)
  }

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text)
    if (type === 'apiKey') {
      setShowCopyTooltipApiKey(true)
    } else if (type === 'webhook') {
      setShowCopyTooltipWebhook(true)
    }
  }

  return (
    <div className={classes.root}>
      <Box padding={2}>
        <Button
          variant='outlined'
          onClick={() => history.push(ROUTES.SETTINGS)}
          size='small'
        >
          Back
        </Button>
        <Box marginTop={2}>
          <Paper>
            <Box padding={2}>
              <Grid container spacing={2} alignItems='center'>
                <Grid item xs={2}>
                  <img
                    className={classes.logoImg}
                    alt='mailwizz_logo'
                    src={mailwizzLogoImg}
                  />
                </Grid>
                <Grid item xs={10}>
                  <Typography variant='h5' gutterBottom>
                    Mailwizz Source
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Box>

        <Box marginTop={2}>
          <Paper>
            <Box padding={2}>
              <Grid container spacing={2}>
                <Grid item xs={2}>
                  <Typography variant='h6'>
                    Name
                  </Typography>
                </Grid>
                <Grid item xs={10}>
                  <Box height='100%' display='flex' alignItems='center'>
                    <Typography variant='body2'>
                      {isLoadingIntegration ? <Skeleton variant='text' width={100} /> : `${integration.nickname}`}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Box>

        {/* API Key */}
        <Box marginTop={2}>
          <Paper>
            <Box padding={2}>
              <Grid container spacing={2}>
                <Grid item xs={2}>
                  <Typography variant='h6'>
                    API Key
                  </Typography>
                </Grid>
                <Grid item xs={10}>
                  <Box display='flex' alignItems='center'>
                    {isLoadingIntegration ? <Skeleton variant='text' width='100%' /> : (
                      <Box display='flex' flexDirection='row' width='100%'>
                        <InputBase
                          variant='filled'
                          value={maskFromPreview(integration.api_key_preview, 'first4-16stars-last4')}
                          size='small'
                          fullWidth
                          disabled
                        />
                        <Tooltip
                          open={showCopyTooltipApiKey}
                          onClose={() => setShowCopyTooltipApiKey(false)}
                          leaveDelay={2000}
                          placement='right'
                          title='Copied'
                        >
                          <Button
                            variant='text'
                            color='primary'
                            onClick={() => copyRevealedCredential({
                            kind: 'integrations',
                            id,
                            field: 'api_key',
                            getToken: () => firebase.auth().currentUser.getIdToken(false),
                          }, (value) => copyToClipboard(value, 'apiKey'))}
                          >
                            Copy
                          </Button>
                        </Tooltip>
                      </Box>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Box>

        {/* Timezone Selector */}
        <Box marginTop={2}>
          <Paper>
            <Box padding={2}>
              <Grid container spacing={2}>
                <Grid item xs={2}>
                  <Typography variant='h6'>
                    Timezone
                  </Typography>
                </Grid>

                <Grid item xs={10}>
                  <Typography variant='body2' gutterBottom>
                    Your Mailwizz Account provides data in the same timezone that your account profile is set to. This value must be set properly below to ensure that times are interpreted correctly. Incorrectly setting this value can result in payment times being incorrectly offset from the true value.
                    The timezone can be found in Mailwizz by logging in, clicking on the profile menu at the top right, then selecting &quot;Settings&quot;.
                    On the page, find the &quot;Select your timezone&quot; to see the current value. Ensure the value selected below matches this timezone.
                  </Typography>
                  <Typography variant='body2' gutterBottom>
                    It is strongly recommended that you do NOT change this value in your Mailwizz account after setting it below, or it can cause date and time inconsistencies that could affect the integrity of the data.
                  </Typography>

                  <Box marginTop={0} display='flex' alignItems='center'>
                    <FormControl
                      variant='outlined'
                      margin='dense'
                      sx={{ margin: 1, minWidth: 300 }}
                      required
                     >
                      {isLoadingIntegration ? <Skeleton variant='rect' width={300} /> : (
                        <Autocomplete
                          id='timezone'
                          options={moment.tz.names()}
                          value={timezone}
                          onChange={event => handleChangeTimezone(event.target.textContent)}
                          renderInput={(params) => <TextField {...params} label='Timezone' />}
                          loading={isUpdatingTimezone}
                        />
                      )}
                    </FormControl>
                  </Box>

                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Box>
        {/* Connector */}
        <Box marginTop={2}>
          <Paper>
            <Box padding={2}>
              <Grid container spacing={2}>
                <Grid item xs={2}>
                  <Typography variant='h6'>
                    Contact Connector
                  </Typography>
                </Grid>
                <Grid item xs={10}>
                  <Box display='block'>
                    <Typography variant='body2' paragraph>
                      Contact Connectors are used to store mappings of contact IDs and email addresses from your email service providers.
                      When using our affiliate ID tracking system, this allows LTV Numbers to match contacts based on their IDs
                      so sales can be attributed to the correct contact. This makes it possible to include affiliate sales in Lifetime Value
                      analysis and other insightful reports.
                    </Typography>
                    <Typography variant='body2' paragraph>
                      It is recommended to decide whether you will be using the connector at the time of creating this integration. The reason is that webhook URL
                      you will add to your Mailwizz account will change when the connector is enabled. If you decide to enable the connector later, you will need to update
                      the webhook URL in your Mailwizz account.
                    </Typography>
                  </Box>
                  {isLoadingConnector ? (
                    <Skeleton variant='rect' width='100%' height={100} />
                  ) : (
                    <>
                      {connector.id ? (
                        <Box
                          display='inline-block'
                          marginTop={1}
                          marginBottom={1}
                          border={1}
                          borderColor={theme.palette.divider}
                          borderRadius={1}
                          padding={2}
                        >
                          <Typography variant='body2' display='block'>
                            <b>Status:</b> Enabled
                          </Typography>
                          <Typography variant='body2' display='block'>
                            <b>Created:</b> {new Date(connector.record_created_at).toLocaleString()}
                          </Typography>
                          <Button
                            variant='text'
                            color='error'
                            onClick={() => setShowDeleteConnectorConfirmation(true)}
                          >
                            Delete Connector
                          </Button>
                        </Box>
                      ) : (
                        <>
                          <Typography variant='caption' display='block'>
                            <b>Status:</b> Disabled
                          </Typography>
                          <Button
                            variant='contained'
                            color='secondary'
                            onClick={() => insertConnector()}
                          >
                            {isEditingConnector ? <CircularProgress color='inherit' size={24} /> : 'Attach Connector'}
                          </Button>
                        </>
                      )}
                    </>
                  )}
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Box>

        <Box marginTop={2}>
          <Paper>
            <Box padding={2}>
              <Grid container spacing={2}>
                <Grid item xs={2}>
                  <Typography variant='h6'>
                    Webhook URL
                  </Typography>
                </Grid>
                <Grid item xs={10}>
                  <Box display='block'>
                    <Typography variant='body2' paragraph>
                      To bring in contact data in real time, use the webhook URL below. One webhook must be created for each list you want to sync contacts from.
                    </Typography>
                    <Typography variant='body2' paragraph>
                      To create a webhook, go to <b>Automations</b> then press <b>Create new</b>. Give the automation a name like &quot;LTV Numbers list subscribe webhook - [List Name]&quot; and press save.
                    </Typography>
                    <Typography variant='body2' paragraph>
                      This will take you to the automation builder. Add the trigger <b>List subscription</b>, and select the list you want to sync contacts from.
                      Leave <b>Include existing subscribers</b> set to No.
                      Next, add the action <b>Call webhook</b>. For the endpoint, copy the webhook URL below and paste it there. Set Method to be <b>POST</b>.
                      For fields, add the following two:
                      <ol>
                        <li>field = email, value = [EMAIL]</li>
                        <li>field = list_uid, value = find the List Unique ID from the <b>Lists</b> page in the first column under <b>Unique ID</b></li>
                      </ol>
                    </Typography>
                    <Typography variant='body2' paragraph>
                      Press <b>Save</b> and then click the back arrow in the top left to go back to the automations list. At this point, it is very important that you click the <b>Activate</b> button
                      on the right side of the automation in the list to turn it on. Without activating the automation, the webhook will not be called when new contacts are added to the list.
                    </Typography>
                    <Typography variant='body2' paragraph>
                      IMPORTANT: you must follow the above steps for each list you want to sync contacts from. Each list will need its own webhook. If you create new lists in the future,
                      you will need to add webhooks for those lists as well. This process also has a lot of room for human error, so please be very careful to follow each step exactly as described,
                      and ensure that you change the values as needed from one list to the next.
                    </Typography>
                  </Box>
                  <Box display='flex' alignItems='center'>
                    {isLoadingIntegration || isLoadingConnector ? <Skeleton variant='text' width='100%' /> : (
                      <Box display='flex' flexDirection='row' width='100%'>
                        <InputBase
                          variant='filled'
                          value={webhookUrl}
                          size='small'
                          fullWidth
                          disabled
                        />
                        <Tooltip
                          open={showCopyTooltipWebhook}
                          onClose={() => setShowCopyTooltipWebhook(false)}
                          leaveDelay={2000}
                          placement='right'
                          title='Copied'
                        >
                          <Button
                            variant='text'
                            color='primary'
                            onClick={() => copyToClipboard(webhookUrl, 'webhook')}
                          >
                            Copy
                          </Button>
                        </Tooltip>
                      </Box>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Box>

        <Box marginTop={2}>
          <Paper>
            <Box padding={2}>
              <Grid container spacing={2}>
                <Grid item xs={2}>
                  <Typography variant='h6'>
                    Delete
                  </Typography>
                </Grid>
                <Grid item xs={10}>
                  <Typography variant='body2' gutterBottom>
                    If you want to remove this data integration, click the button below.
                  </Typography>
                  <Button
                    className={classes.redButton}
                    variant='text'
                    onClick={() => setShowRevokeConfirmation(true)}
                  >
                    Delete Source
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Box>
      </Box>

      {/* Delete Source Confirmation Dialog */}
      <Dialog
        open={showRevokeConfirmation}
        onClose={() => setShowRevokeConfirmation(false)}
      >
        <DialogTitle>
          Remove access to account <b>&quot;{integration.nickname}&quot;</b>?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will prevent data from being synced to LTV Numbers.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            className={classes.redButton}
            variant='text'
            onClick={() => setShowRevokeConfirmation(false)}
          >
            Cancel
          </Button>
          <Button
            variant='contained'
            color='primary'
            onClick={() => revoke()}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Connector Confirmation Dialog */}
      <Dialog
        open={showDeleteConnectorConfirmation}
        onClose={() => setShowDeleteConnectorConfirmation(false)}
      >
        <DialogTitle>
          Remove Contact Connector from <b>&quot;{integration.nickname}&quot;</b>?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will cause the current webhook URL to become invalid, preventing new contacts
            and their IDs from syncing to LTV Numbers. It will also delete all contact mappings
            that have been saved for this connector. This cannot be undone. If you are going to proceed, you should first delete
            the webhook from your Mailwizz account. Are you sure you want to remove the connector?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            className={classes.redButton}
            variant='text'
            onClick={() => setShowDeleteConnectorConfirmation(false)}
          >
            Cancel
          </Button>
          <Button
            variant='contained'
            color='primary'
            onClick={() => deleteConnector()}
          >
            {isEditingConnector ? <CircularProgress color='inherit' size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        open={showMessage}
        autoHideDuration={10000}
        onClose={handleCloseMessage}
      >
        <Alert
          severity={messageSeverity}
          variant='filled'
          onClose={handleCloseMessage}
        >
          {message}
        </Alert>
      </Snackbar>
    </div>
  )
}

MailwizzSource.propTypes = {}

export default MailwizzSource